import type { GutScoreInput } from '@qualicloud/contracts';
import { DEFAULT_CRITICALITY_THRESHOLDS, type CriticalityThresholds } from '@qualicloud/domain';
import { useId } from 'react';
import { Card, RadioGroup, cn } from '@/design-system';
import { GUT_FACTORS, previewCriticality, type GutFactor } from '@/lib/sgq/presentation';
import { CriticalityBadge } from './sgq-badges';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * MATRIZ GUT — Gravidade × Urgência × Tendência
 * sgq.md §A.2
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * O QUE ESTE COMPONENTE RESOLVE
 * No legado a matriz eram três `<select>` de 1 a 5 cujo texto do nível ficava
 * escondido dentro da lista fechada (`qualidade.twig:183-220`): para comparar
 * "Muito grave" com "Grave" o usuário precisava abrir e fechar o combo três
 * vezes. Pior, a criticidade resultante era calculada por JavaScript num campo
 * `readonly` e a FAIXA exibida ao lado dependia de qual tela estava aberta — a
 * grade usava `≤5 / 6-10 / >10` e o resto do sistema `1-20 / 21-40 / ≥41`
 * (BUG-NC-02). Duas telas, duas respostas para a mesma NC.
 *
 * Aqui os quinze níveis ficam VISÍVEIS ao mesmo tempo, cada um com a explicação
 * do que significa, e o score e a faixa são calculados por `gutScore` e
 * `criticalityFromScore` de `@qualicloud/domain` — as MESMAS funções que o CORE
 * usa ao gravar. Não há segunda implementação para divergir.
 *
 * ACESSIBILIDADE
 *  · cada fator é um `<RadioGroup>` com legenda própria: o leitor de tela
 *    anuncia "Gravidade, Muito grave, 2 de 5" em vez de "opção 2 de 5";
 *  · navegação por setas com rolagem circular, padrão APG, vinda do Radix;
 *  · a descrição de cada nível é `aria-describedby` do rádio, não tooltip —
 *    tooltip não é alcançável por teclado em leitura sequencial;
 *  · o resultado vive num `aria-live="polite"`: escolher um nível ANUNCIA a nova
 *    pontuação, senão quem não enxerga muda a gravidade e não sabe o efeito;
 *  · a faixa nunca é só cor — `<CriticalityBadge>` traz ícone e texto.
 */

export interface GutMatrixProps {
  readonly value: Partial<GutScoreInput>;
  readonly onChange: (value: Partial<GutScoreInput>) => void;
  readonly disabled?: boolean;
  /** Erros por fator, vindos do `formState` do React Hook Form. */
  readonly errors?: Partial<Record<GutFactor, string>>;
  /** Limiares da organização. Default: 1–20 baixa, 21–40 média, ≥41 alta. */
  readonly thresholds?: CriticalityThresholds;
  /**
   * Título do bloco. `Reavaliação da matriz GUT` na tela do gestor — a GUT do
   * gestor vai para coluna SEPARADA (`manager_criticality_score`), preservando
   * a do emissor.
   */
  readonly legend?: string;
  readonly description?: string;
  readonly className?: string;
}

export function GutMatrix({
  value,
  onChange,
  disabled = false,
  errors,
  thresholds = DEFAULT_CRITICALITY_THRESHOLDS,
  legend = 'Matriz GUT',
  description = 'Avalie os três fatores de 1 a 5. A criticidade é o produto Gravidade × Urgência × Tendência e varia de 1 a 125.',
  className,
}: GutMatrixProps) {
  const resultId = useId();

  const complete =
    isFactorValue(value.severity) && isFactorValue(value.urgency) && isFactorValue(value.trend);

  const presentation = complete
    ? previewCriticality(
        {
          severity: value.severity as number,
          urgency: value.urgency as number,
          trend: value.trend as number,
        },
        thresholds,
      )
    : null;

  return (
    <Card className={cn('flex flex-col gap-4', className)}>
      <div>
        <h3 className="text-base font-semibold text-content">{legend}</h3>
        <p className="mt-1 text-sm text-content-muted">{description}</p>
      </div>

      <div className="grid gap-5 lg:grid-cols-3">
        {GUT_FACTORS.map((factor) => (
          <div key={factor.factor} className="min-w-0">
            <RadioGroup<string>
              legend={
                <>
                  {/* Rótulo e pergunta em elementos SEPARADOS: assim o nome do
                      fator é um nó de texto próprio, encontrável tanto por
                      leitor de tela quanto por consulta de teste, em vez de
                      ficar concatenado com a frase de apoio. */}
                  <span className="block">{factor.label}</span>
                  <span className="mt-0.5 block text-xs font-normal text-content-subtle">
                    {factor.question}
                  </span>
                </>
              }
              value={
                isFactorValue(value[factor.factor])
                  ? String(value[factor.factor])
                  : undefined
              }
              onValueChange={(next) =>
                onChange({ ...value, [factor.factor]: Number(next) })
              }
              disabled={disabled}
              error={errors?.[factor.factor]}
              options={factor.levels.map((level) => ({
                value: String(level.value),
                label: (
                  <span className="flex items-baseline gap-1.5">
                    <span
                      aria-hidden="true"
                      className="w-3 shrink-0 text-xs font-semibold text-content-subtle qc-tabular"
                    >
                      {level.value}
                    </span>
                    <span>{level.label}</span>
                  </span>
                ),
                description: level.description,
              }))}
            />
          </div>
        ))}
      </div>

      {/*
        RESULTADO EM TEMPO REAL.

        `aria-live="polite"` e não `assertive`: o usuário está navegando por
        setas dentro de um grupo de rádio, e interromper a cada tecla tornaria a
        matriz inutilizável no leitor de tela. Polite anuncia quando ele para.
      */}
      <div
        id={resultId}
        aria-live="polite"
        className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-surface-sunken px-3 py-3"
      >
        <div className="min-w-0">
          <p className="text-2xs font-semibold uppercase tracking-wide text-content-subtle">
            Criticidade calculada
          </p>
          {presentation ? (
            <p className="mt-0.5 text-sm text-content">
              <span className="qc-tabular font-semibold">
                {value.severity} × {value.urgency} × {value.trend} = {presentation.score}
              </span>{' '}
              <span className="text-content-muted">
                — faixa {presentation.label.toLowerCase()}
              </span>
            </p>
          ) : (
            <p className="mt-0.5 text-sm text-content-muted">
              Escolha um nível em cada um dos três fatores para ver a criticidade.
            </p>
          )}
        </div>

        {presentation ? (
          <CriticalityBadge
            variant="chip"
            score={presentation.score}
            level={presentation.level}
          />
        ) : null}
      </div>

      <p className="text-xs text-content-subtle">
        Faixas desta organização: até {thresholds.lowMax} baixa, de {thresholds.lowMax + 1} a{' '}
        {thresholds.mediumMax} média, acima de {thresholds.mediumMax} alta.
      </p>
    </Card>
  );
}

function isFactorValue(value: number | undefined): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 1 && value <= 5;
}
